/* Repository path: frontend/src/components/FeedbackPanel.tsx */

import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import { submitFeedback } from "../api/feedback";
import type { FeedbackAccessContext, FeedbackCategory, FeedbackSurface, PlanItem } from "../types";
import { PRODUCT_NAME } from "../utils/productName";
import { Button } from "./Button";
import { ChoiceGroup, type Choice } from "./ChoiceGroup";

const MAX_MESSAGE_LENGTH = 2000;
const COUNTER_THRESHOLD = 1800;

const CATEGORY_CHOICES: Choice[] = [
  { value: "missing", label: "Something is missing" },
  { value: "wrong", label: "Something seems wrong" },
  { value: "confusing", label: "I don’t understand this" },
  { value: "idea", label: "Feature idea" },
  { value: "other", label: "Other" },
];

const RATING_CHOICES: Choice[] = [
  { value: "1", label: "1 — Not useful" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5 — Very useful" },
];

type Phase = "collapsed" | "open" | "sent";
type PendingFocus = "trigger" | "category" | null;
type PanelError = { kind: "offline" | "rate_limited" | "generic"; message: string };

const RATE_LIMITED_MESSAGE =
  "You’ve sent a few notes just now. Give it a minute and we’ll take the next one.";
const OFFLINE_MESSAGE =
  "Connect to the internet to send this. What you’ve typed stays here until you navigate away.";
const GENERIC_ERROR_MESSAGE = "We couldn’t send that just now. Your note is still here — try again.";

/**
 * Focuses the category `ChoiceGroup`'s fieldset when it is focusable
 * (`ChoiceGroup` only sets `tabIndex={-1}` on the fieldset while it is
 * showing a field error), and otherwise falls back to the first radio —
 * the case that matters for "Send more feedback", which returns focus to
 * an empty, error-free form. See docs/design/features/W4-08.md
 * "Responsive/accessibility".
 */
function focusCategoryGroup(groupId: string): void {
  const root = document.getElementById(groupId);
  if (!root) return;
  root.focus();
  if (document.activeElement !== root) {
    root.querySelector<HTMLInputElement>('input[type="radio"]')?.focus();
  }
}

export interface FeedbackPanelProps {
  surface: FeedbackSurface;
  accessContext: FeedbackAccessContext;
  /**
   * Present only when launched from a card or an item detail — named on
   * screen so the person knows what the note is about, but only its
   * `code` is ever sent. See docs/architecture/features/W4-08.md §3 and
   * docs/design/features/W4-08.md "UI/component delta".
   */
  item?: PlanItem;
  online: boolean;
  announce: (message: string) => void;
}

/**
 * The anonymous, in-product feedback panel (W4-08). Opens in place, never
 * navigates, never blocks the plan behind it, and is never a modal, a
 * route or a viewport-pinned tab — see docs/design/features/W4-08.md
 * "The one thing this design has to get right". Owns its own errors
 * (`role="alert"` inside the panel); never uses the page-level
 * `ErrorSummary`. Writes nothing to `localStorage` or `sessionStorage`.
 */
export function FeedbackPanel({ surface, accessContext, item, online, announce }: FeedbackPanelProps) {
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const bodyId = `${baseId}-body`;
  const triggerId = `${baseId}-trigger`;
  const categoryGroupId = `${baseId}-category`;
  const messageId = `${baseId}-message`;
  const messageHintId = `${baseId}-message-hint`;
  const ratingGroupId = `${baseId}-rating`;

  const [phase, setPhase] = useState<Phase>("collapsed");
  // A one-shot focus request. Held in a ref rather than state: the effect
  // below that consumes it must clear it after acting, and clearing state
  // from inside the very effect it triggers is exactly the cascading-render
  // pattern react-hooks/set-state-in-effect exists to catch. Clearing a ref
  // is not a state update, so it carries no such warning. `focusRequest` is
  // the state half of the pair — it exists only so that assigning the ref
  // has something to make an effect re-run on; its value is never read.
  const pendingFocusRef = useRef<PendingFocus>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const [category, setCategory] = useState("");
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<PanelError | null>(null);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const confirmHeadingRef = useRef<HTMLHeadingElement>(null);

  function requestFocus(target: Exclude<PendingFocus, null>) {
    pendingFocusRef.current = target;
    setFocusRequest((count) => count + 1);
  }

  // Opening moves focus to the (already-present) section heading; arriving
  // at the confirmation moves it to the confirmation heading. Declared
  // before the pendingFocusRef effect below so that on the "Send more
  // feedback" transition (phase: sent -> open, a focus request for
  // "category" made in the same tick) the category group's focus wins over
  // this one - effects run in declaration order after each commit.
  useEffect(() => {
    if (phase === "open") headingRef.current?.focus();
    else if (phase === "sent") confirmHeadingRef.current?.focus();
  }, [phase]);

  useEffect(() => {
    const target = pendingFocusRef.current;
    if (target === "trigger") document.getElementById(triggerId)?.focus();
    else if (target === "category") focusCategoryGroup(categoryGroupId);
    pendingFocusRef.current = null;
    // focusRequest exists only to make this effect re-run once per
    // requestFocus call; its own value is never read. pendingFocusRef is a
    // ref, not reactive state, so react-hooks/exhaustive-deps correctly
    // does not require it in the dependency array below.
  }, [focusRequest, categoryGroupId, triggerId]);

  function resetForm() {
    setCategory("");
    setCategoryError(null);
    setMessage("");
    setRating("");
    setError(null);
  }

  const openPanel = () => {
    resetForm();
    setPhase("open");
  };

  const cancel = () => {
    resetForm();
    setPhase("collapsed");
    requestFocus("trigger");
  };

  const sendMore = () => {
    resetForm();
    setPhase("open");
    requestFocus("category");
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!category) {
      setCategoryError("Choose what this is about so we know where to look.");
      requestFocus("category");
      return;
    }
    setCategoryError(null);
    setError(null);
    setSubmitting(true);
    announce("Sending feedback");
    try {
      await submitFeedback({
        category: category as FeedbackCategory,
        surface,
        accessContext,
        itemCode: item?.code ?? null,
        message: message.trim() ? message.trim() : null,
        rating: rating ? Number(rating) : null,
      });
      setPhase("sent");
      announce("Feedback sent");
    } catch (err) {
      if (err instanceof ApiError && err.code === "offline") {
        setError({ kind: "offline", message: OFFLINE_MESSAGE });
      } else if (
        err instanceof ApiError
        && (err.status === 429 || err.code === "rate_limited" || err.code === "rate_limit_unavailable")
      ) {
        setError({ kind: "rate_limited", message: RATE_LIMITED_MESSAGE });
      } else {
        setError({ kind: "generic", message: GENERIC_ERROR_MESSAGE });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="feedback-panel" aria-labelledby={titleId}>
      <p className="eyebrow">Beta</p>
      <h2 id={titleId} ref={headingRef} tabIndex={-1}>
        Help us improve this plan
      </h2>
      <p>
        {PRODUCT_NAME} is in beta. If something here is confusing, wrong or missing, telling us
        is the fastest way it gets fixed.
      </p>

      {phase === "collapsed" && (
        <Button
          type="button"
          variant="quiet"
          id={triggerId}
          aria-expanded={false}
          aria-controls={bodyId}
          onClick={openPanel}
        >
          Tell us what’s wrong or missing
        </Button>
      )}

      <div id={bodyId} hidden={phase === "collapsed"}>
        {phase === "sent" && (
          <div className="confirmation confirmation--success" role="status">
            <h3 ref={confirmHeadingRef} tabIndex={-1}>
              Thanks — we’ve got it.
            </h3>
            <p>
              We read every note while {PRODUCT_NAME} is in beta. This is anonymous, so we can’t
              reply — if you need an answer about your own situation, use the official source
              linked on each item.
            </p>
            <Button type="button" variant="quiet" onClick={sendMore}>
              Send more feedback
            </Button>
          </div>
        )}

        {phase === "open" && (
          <form onSubmit={(event) => void handleSubmit(event)}>
            {item && (
              <p className="feedback-panel__about">
                About: {item.official_name}{" "}
                <span className="estimate-card__gloss">({item.english_name})</span>
              </p>
            )}

            <ChoiceGroup
              id={categoryGroupId}
              legend="What’s this about?"
              choices={CATEGORY_CHOICES}
              value={category}
              onChange={(value) => {
                setCategory(value);
                setCategoryError(null);
              }}
              error={categoryError ?? undefined}
            />

            <div className="field feedback-panel__field">
              <label htmlFor={messageId}>Anything you’d like to add? (optional)</label>
              <textarea
                id={messageId}
                rows={4}
                maxLength={MAX_MESSAGE_LENGTH}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                aria-describedby={messageHintId}
              />
              <div id={messageHintId}>
                <p className="field-hint">
                  <strong>Please leave out personal details.</strong> Don’t include your name,
                  address, income, or anything from your answers — this reaches us as an
                  anonymous note, so we can’t remove it later on your behalf.
                </p>
                {message.length >= COUNTER_THRESHOLD && (
                  <p className="field-hint feedback-panel__counter">
                    {MAX_MESSAGE_LENGTH - message.length} characters left.
                  </p>
                )}
              </div>
            </div>

            <ChoiceGroup
              id={ratingGroupId}
              legend="How useful is this plan to you so far? (optional)"
              choices={RATING_CHOICES}
              value={rating}
              onChange={setRating}
            />

            {error && (
              <p role="alert" className="field-error">
                {error.message}
              </p>
            )}

            <div className="action-row">
              <Button type="submit" busy={submitting} disabled={!online || submitting}>
                {submitting ? "Sending…" : "Send feedback"}
              </Button>
              <Button type="button" variant="quiet" onClick={cancel} disabled={submitting}>
                Cancel
              </Button>
            </div>

            {!online && (
              <p className="field-hint" role="status">
                {OFFLINE_MESSAGE}
              </p>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
